import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';

import Modal from '../../../../../shared/_TBComponents/Modal';
import Icon from '../../../../../shared/_TBComponents/Icon';
import Spinner from '../../../../../shared/_TBComponents/Spinner';
import { showErrorToast, showSuccessToast } from '../../../../../shared/utils/toastManager';
import { decodeVideoUrl } from '../../../../../shared/utils/decodeHtmlEntities';
import resolveIframelyEmbed from '../../../../../shared/utils/resolveIframelyEmbed';
import ModalHeading from '../ModalHeading';
import TagModalShell from './TagModalShell';
import {
  createExercise,
  updateExercise,
  fetchExerciseDetailForAccount,
} from '../../../../calendar/ducks/calendarActions';

// A progression is an exercise row with type 'P'; its levels/steps ride the
// same POST/PATCH exercise endpoints. Level/step numbering is positional.

const Body = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
  font-family: "Nunito Sans";
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #D6D6D6;
`;

const Divider = styled.div`
  height: 1px;
  background: #D6D6D6;
  width: 100%;
`;

// Modal runs customPadding='0' for its full-bleed dividers, so each
// section insets itself instead.
const InsetSection = styled.div`
  padding: 0 24px;
`;

const BodySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const FieldLabel = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
`;

const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: #FFFFFF;
  border: 2px solid #D6D6D6;
  border-radius: 4px;
  padding: 8px 16px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
  outline: none;

  ::placeholder { color: #929292; }
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  height: 64px;
  resize: vertical;

  /* Doubled class beats the shared Modal's short-viewport media rule
     (.modal textarea { min-height: 120px }), which would balloon this field. */
  && { min-height: 64px; }
  background: #FFFFFF;
  border: 2px solid #D6D6D6;
  border-radius: 4px;
  padding: 8px 16px;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
  outline: none;

  ::placeholder { color: #929292; }
`;

// Levels can get tall — scroll them inside the modal instead of growing it
// past the viewport.
const LevelsScroll = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LevelBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LevelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LevelTitle = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
  color: #1A1A1A;
`;

const LevelCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #F6F6F6;
  border-radius: 4px;
  padding: 8px 16px;
`;

const NewStepButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 6px 16px;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.39px;
  color: #1787FB;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const NewLevelButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  background: rgba(23, 135, 251, 0.1);
  border: 1px dashed #1787FB;
  border-radius: 4px;
  padding: 6px 16px;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.39px;
  color: #1787FB;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  cursor: pointer;

  input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  input:focus-visible + svg { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const CheckboxText = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
`;

/* The design's 24px checkbox (18px glyph) — checked #1787FB, unchecked
   #D6D6D6 outline. */
const CheckboxGlyph = ({ checked }) => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
    {checked ? (
      <path transform='translate(3 3)' d='M15 0C16.6569 0 18 1.34315 18 3V15C18 16.6569 16.6569 18 15 18H3C1.34315 18 0 16.6569 0 15V3C2.57708e-07 1.34315 1.34315 1.28852e-07 3 0H15ZM3 2C2.44772 2 2 2.44772 2 3V15C2 15.5523 2.44772 16 3 16H15C15.5523 16 16 15.5523 16 15V3C16 2.44772 15.5523 2 15 2H3ZM11.6914 5.38965C12.0284 4.94809 12.6598 4.86338 13.1016 5.2002C13.5431 5.53715 13.6278 6.16863 13.291 6.61035L8.72168 12.6104C8.53297 12.8555 8.24097 12.9991 7.93164 13C7.62398 13.0017 7.33243 12.8614 7.1416 12.6201L4.71094 9.50977C4.37166 9.07365 4.44999 8.44515 4.88574 8.10547C5.32205 7.76581 5.95136 7.84397 6.29102 8.28027L7.9209 10.3896L11.7012 5.38965H11.6914Z' fill='#1787FB' />
    ) : (
      <path transform='translate(3 3)' fillRule='evenodd' clipRule='evenodd' d='M15 18H3C1.34315 18 0 16.6568 0 15V3C0 1.34315 1.34315 0 3 0H15C16.6568 0 18 1.34315 18 3V15C18 16.6568 16.6568 18 15 18ZM3 2C2.44771 2 2 2.44771 2 3V15C2 15.5523 2.44771 16 3 16H15C15.5523 16 16 15.5523 16 15V3C16 2.44771 15.5523 2 15 2H3Z' fill='#D6D6D6' />
    )}
  </svg>
);

CheckboxGlyph.propTypes = { checked: PropTypes.bool.isRequired };

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

/* Shared Spinner defaults to a 65px ring; sized down here for an inline
   adornment. Arc uses currentColor so it matches its surrounding context. */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const InlineSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border: 2px solid rgba(146, 146, 146, 0.25);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const VideoInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

// Room for the check/cross/spinner status glyph the wrapper overlays.
const VideoTextInput = styled(TextInput)`
  padding-right: 40px;
`;

const VideoStatus = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
`;

// A pasted video URL is resolved to its embeddable player link via Iframely;
// the embed link (not the raw URL) is what's saved. Unresolvable URLs block save.
const resolveVideoEmbedUrl = async (url) => {
  const { playerUrl } = await resolveIframelyEmbed(url);
  return playerUrl;
};

const emptyStep = () => ({
  instructions: '', video: '', videoEmbed: '', videoStatus: null, compOnly: false,
});
const emptyLevel = () => ({ steps: [emptyStep()] });

const ProgressionModal = ({
  isOpen, onClose, accountCode, progression, onSaved,
}) => {
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [levels, setLevels] = useState([emptyLevel()]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Guards against a slow detail fetch resolving after the modal was closed
  // (or reopened for a different row) and clobbering the form.
  const loadTokenRef = useRef(0);
  const levelsScrollRef = useRef(null);
  const prevLevelCountRef = useRef(0);
  // Per-step debounce timers and request ids for the video URL check, keyed
  // `${levelIndex}-${stepIndex}`. The id guards against a stale response landing late.
  const videoTimersRef = useRef({});
  const videoReqRef = useRef({});

  useEffect(() => () => {
    Object.values(videoTimersRef.current).forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    Object.values(videoTimersRef.current).forEach(clearTimeout);
    videoTimersRef.current = {};
    videoReqRef.current = {};
    setName(progression?.name || '');
    prevLevelCountRef.current = 1;
    setLevels([emptyLevel()]);
    setIsSaving(false);
    if (!progression) {
      setIsLoading(false);
      return;
    }
    loadTokenRef.current += 1;
    const token = loadTokenRef.current;
    setIsLoading(true);
    dispatch(fetchExerciseDetailForAccount(accountCode, progression.id, {
      onSuccess: (detail) => {
        if (token !== loadTokenRef.current) return;
        const loaded = (detail?.progressionLevels || []).map((level) => ({
          // Stored videos are already embed links — mirror into videoEmbed so
          // an untouched step round-trips unchanged; videoStatus stays unset.
          steps: (level.steps || []).map((step) => ({
            instructions: step.instructions || '',
            video: decodeVideoUrl(step.video),
            videoEmbed: decodeVideoUrl(step.video),
            videoStatus: null,
            compOnly: !!step.compOnly,
          })),
        }));
        prevLevelCountRef.current = loaded.length || 1;
        setLevels(loaded.length ? loaded : [emptyLevel()]);
        setIsLoading(false);
      },
      onError: () => {
        if (token !== loadTokenRef.current) return;
        setIsLoading(false);
        onClose();
      },
    }));
  }, [isOpen, progression, accountCode]);

  const updateStep = (levelIndex, stepIndex, patch) => {
    setLevels((prev) => prev.map((level, li) => (li === levelIndex
      ? {
        ...level,
        steps: level.steps.map((step, si) => (si === stepIndex ? { ...step, ...patch } : step)),
      }
      : level)));
  };

  // Legacy validated on every keystroke; debounce instead so we don't hit
  // Iframely for each character of a hand-typed URL.
  const handleVideoChange = (levelIndex, stepIndex, value) => {
    const key = `${levelIndex}-${stepIndex}`;
    clearTimeout(videoTimersRef.current[key]);
    const reqId = (videoReqRef.current[key] || 0) + 1;
    videoReqRef.current[key] = reqId;
    const url = value.trim();
    updateStep(levelIndex, stepIndex, {
      video: value,
      videoEmbed: '',
      videoStatus: url ? 'checking' : null,
    });
    if (!url) return;
    videoTimersRef.current[key] = setTimeout(async () => {
      let embed = null;
      try {
        embed = await resolveVideoEmbedUrl(url);
      } catch (err) {
        embed = null;
      }
      if (videoReqRef.current[key] !== reqId) return;
      updateStep(levelIndex, stepIndex, embed
        ? { videoEmbed: embed, videoStatus: 'valid' }
        : { videoStatus: 'invalid' });
    }, 500);
  };

  const addStep = (levelIndex) => {
    setLevels((prev) => prev.map((level, li) => (li === levelIndex
      ? { ...level, steps: [...level.steps, emptyStep()] }
      : level)));
  };

  // Levels/steps can only be added, never removed — matches the legacy
  // modal (and the API, which never deletes existing Progression_Items).
  const addLevel = () => setLevels((prev) => [...prev, emptyLevel()]);

  // A new level renders at the bottom of the scroll area, out of view —
  // scroll it (and the New Level button under it) into view once it exists.
  useLayoutEffect(() => {
    const grew = levels.length > prevLevelCountRef.current;
    prevLevelCountRef.current = levels.length;
    if (grew && levelsScrollRef.current) {
      levelsScrollRef.current.scrollTop = levelsScrollRef.current.scrollHeight;
    }
  }, [levels.length]);

  const handleSave = () => {
    if (isSaving || isLoading) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      showErrorToast('Progression name is required');
      return;
    }
    // Legacy silently saved an empty video when the URL failed validation —
    // block instead so a bad paste can't quietly drop the video.
    const allSteps = levels.flatMap((level) => level.steps);
    if (allSteps.some((step) => step.videoStatus === 'checking')) {
      showErrorToast('Still checking a video URL — try again in a moment');
      return;
    }
    if (allSteps.some((step) => step.videoStatus === 'invalid')) {
      showErrorToast('A video URL is invalid — fix or clear it before saving');
      return;
    }
    const progressionLevels = levels.map((level) => ({
      steps: level.steps.map((step) => ({
        instructions: step.instructions.trim(),
        compOnly: step.compOnly,
        video: step.videoEmbed.trim(),
      })),
    }));
    // Drop trailing all-empty levels so a stray "Add New Level" click doesn't
    // inflate the stored count. Trailing only — dropping an interior level would renumber the rest.
    while (
      progressionLevels.length > 1
      && progressionLevels[progressionLevels.length - 1].steps.every((step) => !step.instructions)
    ) {
      progressionLevels.pop();
    }
    // The API drops steps with empty instructions, so a progression whose
    // steps are all blank would save as empty levels — catch it up front.
    const hasStep = progressionLevels.some(
      (level) => level.steps.some((step) => step.instructions),
    );
    if (!hasStep) {
      showErrorToast('At least 1 step with instructions is required');
      return;
    }

    setIsSaving(true);
    // `silent` suppresses the actions' generic "Exercise …" toasts, so this
    // modal owns both outcomes and can show the real failure reason.
    if (!progression) {
      dispatch(createExercise(accountCode, {
        name: trimmedName,
        type: 'P',
        reusableItem: 1,
        progressionLevels,
      }, {
        silent: true,
        onSuccess: () => {
          showSuccessToast('Progression created');
          setIsSaving(false);
          onSaved();
          onClose();
        },
        onError: (message) => {
          showErrorToast(message || 'Unable to create progression');
          setIsSaving(false);
        },
      }));
      return;
    }
    dispatch(updateExercise(accountCode, progression.id, {
      name: trimmedName,
      progressionLevels,
    }, {
      silent: true,
      onSuccess: () => {
        showSuccessToast('Progression updated');
        setIsSaving(false);
        onSaved();
        onClose();
      },
      onError: (err) => {
        showErrorToast(
          err?.response?.headers?.message
            || err?.response?.data?.error?.message
            || 'Unable to update progression',
        );
        setIsSaving(false);
      },
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      hideTitle
      customMaxWidth='700px'
      customMinHeight='0'
      customPadding='0'
      customBorderRadius='8px'
      hideButton
    >
      {isOpen && (
        <Body>
          <InsetSection>
            <ModalHeading
              title={progression ? 'Edit Progression' : 'New Progression'}
              onClose={onClose}
            />
          </InsetSection>

          <Divider />

          {isLoading ? (
            <SpinnerWrapper><Spinner /></SpinnerWrapper>
          ) : (
            <InsetSection>
              <TagModalShell
                onCancel={onClose}
                onSave={handleSave}
                isSaving={isSaving}
                saveDisabled={isSaving}
                saveLabel={progression ? 'Save' : 'Create Progression'}
              >
                <BodySection>
                  <Field>
                    <FieldLabel>Name</FieldLabel>
                    <TextInput
                      name='progression-name'
                      value={name}
                      placeholder='Progression name'
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Field>
                  <LevelsScroll ref={levelsScrollRef}>
                    {levels.map((level, levelIndex) => (
                    // Levels/steps are identified by position (matches the API),
                    // so index keys are the honest choice here.
                    // eslint-disable-next-line react/no-array-index-key
                      <LevelBlock key={levelIndex}>
                        <LevelHeader>
                          <LevelTitle>{`Level ${levelIndex + 1}`}</LevelTitle>
                          <NewStepButton type='button' onClick={() => addStep(levelIndex)}>
                            <Icon icon='plus' size={16} color='#1787FB' />
                            New Step
                          </NewStepButton>
                        </LevelHeader>
                        <LevelCard>
                          {level.steps.map((step, stepIndex) => (
                          // eslint-disable-next-line react/no-array-index-key
                            <React.Fragment key={stepIndex}>
                              <Field>
                                <FieldLabel>{`Step ${stepIndex + 1}`}</FieldLabel>
                                <TextArea
                                  name={`progression-step-${levelIndex}-${stepIndex}-instructions`}
                                  value={step.instructions}
                                  placeholder='Step instructions'
                                  onChange={(e) => updateStep(levelIndex, stepIndex, {
                                    instructions: e.target.value,
                                  })}
                                />
                              </Field>
                              <Field>
                                <FieldLabel>Video URL</FieldLabel>
                                <VideoInputWrapper>
                                  <VideoTextInput
                                    name={`progression-step-${levelIndex}-${stepIndex}-video`}
                                    value={step.video}
                                    placeholder='Video url'
                                    onChange={(e) => (
                                      handleVideoChange(levelIndex, stepIndex, e.target.value)
                                    )}
                                  />
                                  {step.videoStatus && (
                                  <VideoStatus>
                                    {step.videoStatus === 'checking' && (
                                      <InlineSpinner style={{ color: '#929292' }} />
                                    )}
                                    {step.videoStatus === 'valid' && (
                                      <Icon icon='checkmark' size={14} color='#0DCC8A' />
                                    )}
                                    {step.videoStatus === 'invalid' && (
                                      <Icon icon='remove' size={14} color='#FF3737' />
                                    )}
                                  </VideoStatus>
                                  )}
                                </VideoInputWrapper>
                              </Field>
                              <CheckboxRow>
                                <input
                                  type='checkbox'
                                  checked={step.compOnly}
                                  onChange={(e) => (
                                    updateStep(
                                      levelIndex,
                                      stepIndex,
                                      { compOnly: e.target.checked },
                                    )
                                  )}
                                />
                                <CheckboxGlyph checked={step.compOnly} />
                                <CheckboxText>
                                  Supplemental step (Completion only - No
                                  &quot;mastered&quot; input)
                                </CheckboxText>
                              </CheckboxRow>
                            </React.Fragment>
                          ))}
                        </LevelCard>
                      </LevelBlock>
                    ))}
                    <NewLevelButton type='button' onClick={addLevel}>
                      <Icon icon='plus' size={16} color='#1787FB' />
                      New Level
                    </NewLevelButton>
                  </LevelsScroll>
                </BodySection>

                <Divider />
              </TagModalShell>
            </InsetSection>
          )}
        </Body>
      )}
    </Modal>
  );
};

ProgressionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountCode: PropTypes.number,
  progression: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  onSaved: PropTypes.func,
};

ProgressionModal.defaultProps = {
  accountCode: undefined,
  progression: null,
  onSaved: () => {},
};

export default ProgressionModal;
